/**
 * Scelta rapida della reazione, aperta dal sorriso accanto al messaggio.
 * Come il menu "...": in un portale con posizione fissa, il pannello Messaggi ha una
 * trasformazione e dentro di lui `fixed` non si riferirebbe alla finestra.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Plus } from 'lucide-react';
import { EmojiPickerPopover } from '../shared/EmojiPickerPopover';
import { computeMenuPosition } from './MessageContextMenu';

const QUICK_EMOJIS = ['👍', '❤️', '😂', '😮', '😢', '🙏'];

interface Props {
  /** Emoji gia' messa da noi, evidenziata. */
  current?: string;
  onPick: (emoji: string) => void;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLElement | null>;
  position: 'left' | 'right';
}

export function ReactionPicker({ current, onPick, onClose, anchorRef, position }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const [showAll, setShowAll] = useState(false);

  useLayoutEffect(() => {
    const anchor = anchorRef.current;
    if (!ref.current || !anchor) return;
    const m = ref.current.getBoundingClientRect();
    setCoords(computeMenuPosition(anchor.getBoundingClientRect(), { width: m.width, height: m.height }, { width: window.innerWidth, height: window.innerHeight }, position));
  }, [anchorRef, position]);

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (ref.current?.contains(t) || anchorRef.current?.contains(t)) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); }
    };
    const onScroll = (e: Event) => {
      if (e.target instanceof Node && ref.current?.contains(e.target)) return;
      onClose();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    document.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('scroll', onScroll, true);
    };
  }, [anchorRef, onClose]);

  return createPortal(
    <div
      ref={ref}
      role="menu"
      aria-label="Scegli una reazione"
      onClick={(e) => e.stopPropagation()}
      style={{ position: 'fixed', top: coords?.top ?? -9999, left: coords?.left ?? -9999, visibility: coords ? 'visible' : 'hidden' }}
      className="z-[10001] flex items-center gap-0.5 p-1 rounded-xl bg-white dark:bg-[#252525] border border-[rgba(55,53,47,0.09)] dark:border-white/10 shadow-lg"
    >
      {QUICK_EMOJIS.map((emoji) => (
        <button
          key={emoji}
          type="button"
          onClick={() => { onPick(emoji); onClose(); }}
          aria-label={`Reagisci con ${emoji}`}
          className={`w-8 h-8 rounded-lg text-lg grid place-items-center hover:bg-[rgba(55,53,47,0.06)] dark:hover:bg-white/10 ${
            current === emoji ? 'bg-[#E7F3FF] dark:bg-[#1D3A55]' : ''
          }`}
        >
          {emoji}
        </button>
      ))}
      <div className="relative">
        <button
          type="button"
          onClick={() => setShowAll((v) => !v)}
          aria-label="Altre emoji"
          aria-expanded={showAll}
          className="w-8 h-8 rounded-lg grid place-items-center text-[#787774] hover:bg-[rgba(55,53,47,0.06)] dark:hover:bg-white/10"
        >
          <Plus className="w-4 h-4" />
        </button>
        {showAll && (
          <EmojiPickerPopover
            align={position === 'right' ? 'right' : 'left'}
            onEmojiSelect={(emoji) => { onPick(emoji); onClose(); }}
            onClose={() => setShowAll(false)}
          />
        )}
      </div>
    </div>,
    document.body,
  );
}
